/**
 * @fileoverview Data model to hold the complete configuration collected over the flow.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */
import {EntitySelection, EntitySelectionJson} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/data/entityselection';
import {FeatureSelection, FeatureSelectionJson} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectfeatures/data/featureselection';
import {AppleConfiguration, AppleConfigurationJson, AppleProductionPublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/appleconfiguration';
import {FacebookConfiguration, FacebookConfigurationJson, FacebookPublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/facebookconfiguration';
import {GbpConfiguration, GbpConfigurationJson, GooglePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/gbpconfiguration';
import {YelpConfiguration, YelpConfigurationJson, YelpEnterprisePublisherId, YelpKnowledgePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/yelpconfiguration';
import {ListingConfiguration, ListingConfigurationJson} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/listingsconfiguration';

export class EnterpriseConfiguration {
  /** @return {EnterpriseConfiguration} */
  static empty() {
    return new EnterpriseConfiguration({
      entitySelection: undefined,
      listingConfiguration: undefined,
      featureSelection: undefined,
      gbpConfiguration: undefined,
      facebookConfiguration: undefined,
      appleConfiguration: undefined,
      yelpConfiguration: undefined,
    });
  }

  /**
   * @param {{
   *   entitySelection: (EntitySelection | undefined),
   *   listingConfiguration: (ListingConfiguration | undefined),
   *   featureSelection: (FeatureSelection | undefined),
   *   gbpConfiguration: (GbpConfiguration | undefined),
   *   facebookConfiguration: (FacebookConfiguration | undefined),
   *   appleConfiguration: (AppleConfiguration | undefined),
   *   yelpConfiguration: (YelpConfiguration | undefined),
   * }} params
   */
  constructor(params) {
    /** @type {EntitySelection | undefined} */
    this.entitySelection = params.entitySelection;
    /** @type {ListingConfiguration | undefined} */
    this.listingConfiguration = params.listingConfiguration;
    /** @type {FeatureSelection | undefined} */
    this.featureSelection = params.featureSelection;
    /** @type {GbpConfiguration | undefined} */
    this.gbpConfiguration = params.gbpConfiguration;
    /** @type {FacebookConfiguration | undefined} */
    this.facebookConfiguration = params.facebookConfiguration;
    /** @type {AppleConfiguration | undefined} */
    this.appleConfiguration = params.appleConfiguration;
    /** @type {YelpConfiguration | undefined} */
    this.yelpConfiguration = params.yelpConfiguration;
  }

  /**
   * @return {!EnterpriseConfigurationJson}
   */
  toJSON() {
    if (!this.entitySelection || !this.listingConfiguration) {
      throw new Error('invalid configuration state');
    }

    // If a configuration has been skipped, set it to empty
    const gbpConfig = this.gbpConfiguration?.skipSetup
      ? GbpConfiguration.empty()
      : this.gbpConfiguration;
    const facebookConfig = this.facebookConfiguration?.skipSetup
      ? FacebookConfiguration.empty()
      : this.facebookConfiguration;
    const appleConfig = this.appleConfiguration?.skipSetup
      ? AppleConfiguration.initialConfiguration(this.appleConfiguration?.publisherId ?? AppleProductionPublisherId)
      : this.appleConfiguration;
    const yelpConfig = this.yelpConfiguration?.skipSetup
      ? YelpConfiguration.empty()
      : this.yelpConfiguration;
    const featuresSelection = this.featureSelection?.skipSetup
      ? FeatureSelection.empty()
      : this.featureSelection;

    return {
      'entitySelection': this.entitySelection.toJSON(),
      'listingConfiguration': this.listingConfiguration.toJSON(),
      'featureSelection': featuresSelection?.toJSON(),
      'gbpConfiguration': gbpConfig?.toJSON(),
      'facebookConfiguration': facebookConfig?.toJSON(),
      'appleConfiguration': appleConfig?.toJSON(),
      'yelpConfiguration': yelpConfig?.toJSON(),
    };
  }

  getEnterpriseConfigurationForPublisherId(publisherId) {
    switch (publisherId) {
      case GooglePublisherId:
        return this.gbpConfiguration;
      case FacebookPublisherId:
        return this.facebookConfiguration;
      case AppleProductionPublisherId:
        return this.appleConfiguration;
      case YelpEnterprisePublisherId:
        return this.yelpConfiguration;
      case YelpKnowledgePublisherId:
        return this.yelpConfiguration;
      default:
        return null;
    }
  }
}
/**
 * @dict
 * @typedef {{
 *   entitySelection: EntitySelectionJson,
 *   listingConfiguration: ListingConfigurationJson,
 *   featureSelection: (FeatureSelectionJson | undefined),
 *   gbpConfiguration: (GbpConfigurationJson | undefined),
 *   facebookConfiguration: (FacebookConfigurationJson | undefined),
 *   appleConfiguration: (AppleConfigurationJson | undefined),
 *   yelpConfiguration: (YelpConfigurationJson | undefined),
 * }}
 */
export let EnterpriseConfigurationJson;

export const BingPublisherId = 483;
export const ExtendedNetworkPublisherId = 0;
export const LlmPublisherId = 50000;
